import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import api from '../api';

export default function PurchaseReport() {
  const [from, setFrom] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'));
  const [bills, setBills] = useState([]);
  const [settings, setSettings] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expanded, setExpanded] = useState({});
  const printRef = useRef();

  useEffect(() => {
    fetchData();
    api.get('/settings/get').then(res => setSettings(res.data || {}));
    api.get('/suppliers').then(res => setSuppliers(res.data || []));
    // eslint-disable-next-line
  }, [from, to]);

  const fetchData = async () => {
    const res = await api.get('/purchase-reports', { params: { from, to } });
    setBills(res.data.bills || []);
  };

  // Filtering
  const filteredBills = bills.filter(b =>
    (!filterSupplier || String(b.SupplierID) === String(filterSupplier)) &&
    (!filterStatus || b.Status === filterStatus)
  );

  const getBillSums = (bill) => {
    const items = bill.items || [];
    const taxableAmt = items.reduce((sum, item) => sum + Number(item.Amount || 0), 0);
    const cgst = items.reduce((sum, item) => sum + Number(item.CGST || 0), 0);
    const sgst = items.reduce((sum, item) => sum + Number(item.SGST || 0), 0);
    const igst = items.reduce((sum, item) => sum + Number(item.IGST || 0), 0);
    const totalGST = cgst + sgst + igst;
    const total = taxableAmt + totalGST - Number(bill.Discount || 0);
    return { taxableAmt, cgst, sgst, igst, totalGST, total };
  };

  const grandTotals = filteredBills.reduce(
    (tot, bill) => {
      const s = getBillSums(bill);
      tot.taxable += s.taxableAmt;
      tot.cgst += s.cgst;
      tot.sgst += s.sgst;
      tot.totalGST += s.totalGST;
      tot.total += s.total;
      return tot;
    },
    { taxable: 0, cgst: 0, sgst: 0, totalGST: 0, total: 0 }
  );

  // PDF Export (filtered data)
  const exportPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(settings.BusinessName || 'Business Name', 105, 16, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(settings.Address || '', 105, 23, { align: 'center' });
    if (settings.GSTIN) doc.text(`GSTIN: ${settings.GSTIN}`, 105, 29, { align: 'center' });
    if (settings.Phone) doc.text(`Contact: ${settings.Phone}`, 105, 35, { align: 'center' });
    doc.setFontSize(13);
    doc.setTextColor(22, 88, 189);
    doc.text('Purchase Report', 105, 46, { align: 'center' });
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(10);
    doc.text(
      `Period: ${dayjs(from).format('DD/MM/YYYY')} to ${dayjs(to).format('DD/MM/YYYY')}`,
      105,
      53,
      { align: 'center' }
    );
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 30);

    // Extra filter info
    let filterInfo = '';
    if (filterSupplier) {
      const sup = suppliers.find(s => String(s.SupplierID) === String(filterSupplier));
      filterInfo += `Supplier: ${sup ? sup.Name : ''} `;
    }
    if (filterStatus) filterInfo += `Status: ${filterStatus}`;
    if (filterInfo) doc.text(filterInfo.trim(), 105, 59, { align: 'center' });

    autoTable(doc, {
      startY: 65,
      head: [[
        'Bill No', 'Date', 'Supplier', 'Taxable Amt', 'CGST', 'SGST', 'Total GST', 'Total', 'Status'
      ]],
      body: filteredBills.map(b => {
        const sums = getBillSums(b);
        return [
          b.BillNo,
          b.BillDate ? dayjs(b.BillDate).format('DD/MM/YYYY') : '',
          b.SupplierName,
          `Rs ${sums.taxableAmt.toFixed(2)}`,
          `Rs ${sums.cgst.toFixed(2)}`,
          `Rs ${sums.sgst.toFixed(2)}`,
          `Rs ${sums.totalGST.toFixed(2)}`,
          `Rs ${sums.total.toFixed(2)}`,
          b.Status
        ];
      }),
      theme: 'grid',
      styles: { font: 'helvetica', fontSize: 10, cellPadding: 2.2, halign: 'center' },
      headStyles: { fillColor: [129, 164, 253] },
      foot: filteredBills.length
        ? [[
            { content: 'Grand Totals:', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `Rs ${grandTotals.taxable.toFixed(2)}`, styles: { fontStyle: 'bold' } },
            { content: `Rs ${grandTotals.cgst.toFixed(2)}`, styles: { fontStyle: 'bold' } },
            { content: `Rs ${grandTotals.sgst.toFixed(2)}`, styles: { fontStyle: 'bold' } },
            { content: `Rs ${grandTotals.totalGST.toFixed(2)}`, styles: { fontStyle: 'bold' } },
            { content: `Rs ${grandTotals.total.toFixed(2)}`, styles: { fontStyle: 'bold' } },
            ''
          ]]
        : []
    });

    doc.save(`PurchaseReport_${dayjs(from).format('DDMMYYYY')}_to_${dayjs(to).format('DDMMYYYY')}.pdf`);
  };

  // Excel Export (filtered data)
  const exportExcel = () => {
    const header = [
      [settings.BusinessName || 'Business Name'],
      [settings.Address || ''],
      [`GSTIN: ${settings.GSTIN || ''}`],
      [`Contact: ${settings.Phone || ''}`],
      ['Purchase Report'],
      [`Period: ${dayjs(from).format('DD/MM/YYYY')} to ${dayjs(to).format('DD/MM/YYYY')}`],
      [],
      ['Bill No', 'Date', 'Supplier', 'Taxable Amt', 'CGST', 'SGST', 'Total GST', 'Total', 'Status']
    ];
    const rows = filteredBills.map(b => {
      const sums = getBillSums(b);
      return [
        b.BillNo,
        b.BillDate ? dayjs(b.BillDate).format('DD/MM/YYYY') : '',
        b.SupplierName,
        sums.taxableAmt.toFixed(2),
        sums.cgst.toFixed(2),
        sums.sgst.toFixed(2),
        sums.totalGST.toFixed(2),
        sums.total.toFixed(2),
        b.Status
      ];
    });
    if (filteredBills.length) {
      rows.push([
        '', '', 'Grand Totals',
        grandTotals.taxable.toFixed(2),
        grandTotals.cgst.toFixed(2),
        grandTotals.sgst.toFixed(2),
        grandTotals.totalGST.toFixed(2),
        grandTotals.total.toFixed(2),
        ''
      ]);
    }
    const data = [...header, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'PurchaseReport');
    XLSX.writeFile(wb, `PurchaseReport_${dayjs(from).format('DDMMYYYY')}_to_${dayjs(to).format('DDMMYYYY')}.xlsx`);
  };

  // Print view
  const handlePrint = () => {
    window.print();
  };

  return (
    <div ref={printRef}>
      <div className="d-flex flex-wrap gap-2 align-items-end mb-3 no-print">
        <div>
          <label>From</label>
          <input
            type="date"
            className="form-control"
            value={from}
            onChange={e => setFrom(e.target.value)}
          />
        </div>
        <div>
          <label>To</label>
          <input
            type="date"
            className="form-control"
            value={to}
            onChange={e => setTo(e.target.value)}
          />
        </div>
        <div>
          <label>Supplier</label>
          <select
            className="form-select"
            value={filterSupplier}
            onChange={e => setFilterSupplier(e.target.value)}
          >
            <option value="">All Suppliers</option>
            {suppliers.map(s => (
              <option key={s.SupplierID} value={s.SupplierID}>
                {s.Name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Status</label>
          <select
            className="form-select"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="Paid">Paid</option>
            <option value="Partial">Partial</option>
            <option value="Pending">Pending</option>
          </select>
        </div>
        <button className="btn btn-primary mt-3" onClick={fetchData}>
          Get Report
        </button>
        {filteredBills.length > 0 && (
          <>
            <button className="btn btn-success mt-3" onClick={exportPDF}>Download PDF</button>
            <button className="btn btn-info mt-3 text-white" onClick={exportExcel}>Export Excel</button>
            <button className="btn btn-outline-dark mt-3" onClick={handlePrint}>Print</button>
          </>
        )}
      </div>

      {/* Summary Cards */}
      <div className="row mb-3 d-print-none">
        <div className="col-md-4 mb-2">
          <div className="card border-primary shadow h-100">
            <div className="card-body">
              <div className="text-muted">Total Taxable</div>
              <div className="fs-4 fw-bold">Rs {grandTotals.taxable.toLocaleString()}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-2">
          <div className="card border-success shadow h-100">
            <div className="card-body">
              <div className="text-muted">Total GST</div>
              <div className="fs-4 fw-bold">Rs {grandTotals.totalGST.toLocaleString()}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-2">
          <div className="card border-dark shadow h-100">
            <div className="card-body">
              <div className="text-muted">Grand Total</div>
              <div className="fs-4 fw-bold">Rs {grandTotals.total.toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Bills Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Bill No</th>
              <th>Date</th>
              <th>Supplier</th>
              <th>Taxable Amt</th>
              <th>CGST</th>
              <th>SGST</th>
              <th>Total GST</th>
              <th>Total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredBills.map((b, idx) => {
              const sums = getBillSums(b);
              return (
                <tr key={b.PurchaseBillID}>
                  <td>{idx + 1}</td>
                  <td>{b.BillNo}</td>
                  <td>{b.BillDate ? dayjs(b.BillDate).format('DD/MM/YYYY') : ''}</td>
                  <td>
                    <b>{b.SupplierName}</b><br/>
                    <small>{b.SupplierAddress}</small><br/>
                    <small>{b.SupplierPhone}</small>
                  </td>
                  <td>Rs {sums.taxableAmt.toFixed(2)}</td>
                  <td>Rs {sums.cgst.toFixed(2)}</td>
                  <td>Rs {sums.sgst.toFixed(2)}</td>
                  <td>Rs {sums.totalGST.toFixed(2)}</td>
                  <td>Rs {sums.total.toFixed(2)}</td>
                  <td>
                    <span className={`badge bg-${b.Status === 'Paid' ? 'success' : b.Status === 'Partial' ? 'warning' : 'danger'}`}>
                      {b.Status}
                    </span>
                  </td>
                </tr>
              );
            })}
            {!filteredBills.length && (
              <tr>
                <td colSpan={10} className="text-center">No purchases found for selected filters.</td>
              </tr>
            )}
          </tbody>
          {filteredBills.length > 0 && (
            <tfoot>
              <tr className="fw-bold bg-light">
                <td colSpan={4} className="text-end">Grand Totals:</td>
                <td>Rs {grandTotals.taxable.toFixed(2)}</td>
                <td>Rs {grandTotals.cgst.toFixed(2)}</td>
                <td>Rs {grandTotals.sgst.toFixed(2)}</td>
                <td>Rs {grandTotals.totalGST.toFixed(2)}</td>
                <td>Rs {grandTotals.total.toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Print-specific styles */}
      <style>
        {`
          @media print {
            .no-print, .no-print * {
              display: none !important;
            }
            .d-print-none {
              display: none !important;
            }
            .table-responsive {
              overflow: visible !important;
            }
            .table {
              font-size: 11pt;
            }
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        `}
      </style>
    </div>
  );
}
